import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { FullPageLoader as SpinnerLoader } from '@/components/ui/spinner';

const LoadingContext = createContext(null);

export function LoadingProvider({ children }) {
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState('');
  const suppressRef = useRef(0);

  const show = useCallback((msg) => {
    if (suppressRef.current > 0) return;
    setCount((c) => c + 1);
    if (msg) setMessage(msg);
  }, []);

  const hide = useCallback(() => {
    setCount((c) => Math.max(0, c - 1));
  }, []);

  const isActive = count > 0;
  const isSuppressed = suppressRef.current > 0;

  const suppressWhile = useCallback(async (fn) => {
    suppressRef.current += 1;
    try { return await fn(); } finally { suppressRef.current = Math.max(0, suppressRef.current - 1); }
  }, []);

  const run = useCallback(async (msg, fn) => {
    const owns = Boolean(msg) && count === 0 && suppressRef.current === 0;
    if (owns) show(msg);
    try { return await fn(); } finally { if (owns) hide(); }
  }, [count, show, hide]);

  const value = useMemo(() => ({
    show, hide, isActive, isSuppressed, suppressWhile, run, message,
  }), [show, hide, isActive, isSuppressed, suppressWhile, run, message]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {isActive && <SpinnerLoader message={message} />}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) throw new Error('useLoading must be used within LoadingProvider');
  return ctx;
}

// Vanilla-compatible singleton for incremental migration — React files should use useLoading() instead
export const FullPageLoader = {
  show: () => console.warn('FullPageLoader.show: use useLoading() in React'),
  hide: () => {},
  isActive: () => false,
  isSuppressed: () => false,
  suppressWhile: async (fn) => fn(),
  run: async (msg, fn) => fn(),
};
